import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Container, TextField, Button, Typography, Grid, Paper, Accordion, AccordionSummary, AccordionDetails, Snackbar, Alert, Box, MenuItem, Select, InputLabel, FormControl, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import config from '../config/config'; // Import configuration

const TeamMembers = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [gridApi, setGridApi] = useState(null); // Define gridApi state
  const editSectionRef = useRef(null);

  useEffect(() => {
    fetchTeamMembers();
    fetchDepartments();
    fetchCompanies();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${config.apiBaseUrl}/api/team-members`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
      });
      console.log('Team Member Details:', response.data); // Log the data to verify
      setTeamMembers(response.data);
      if (response.data.length > 0) {
        setSelectedTeamMember(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      setSnackbar({ open: true, message: 'Failed to fetch team members', severity: 'error' });
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${config.apiBaseUrl}/api/departments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
      });
      console.log('Department Details:', response.data); // Log the data to verify
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${config.apiBaseUrl}/api/companies`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
      });
      console.log('Company Details:', response.data); // Log the data to verify
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', event.target.value);
    }
  };

  const handleRowClick = (event) => {
    const teamMember = event.data;
    setSelectedTeamMember(teamMember);
    setTimeout(() => {
      editSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'department') {
      const department = departments.find((dept) => dept.departmentId === value);
      setSelectedTeamMember({ ...selectedTeamMember, department });
    } else if (name === 'company') {
      const company = companies.find((comp) => comp.companyId === value);
      setSelectedTeamMember({ ...selectedTeamMember, company });
    } else {
      setSelectedTeamMember({ ...selectedTeamMember, [name]: value });
    }
  };

  const handleDateChange = (date) => {
    setSelectedTeamMember({ ...selectedTeamMember, relievingDate: date });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (selectedTeamMember.teamMemberId) {
        await axios.put(`${config.apiBaseUrl}/api/team-members/${selectedTeamMember.teamMemberId}`, selectedTeamMember, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
      } else {
        await axios.post(`${config.apiBaseUrl}/api/team-members`, selectedTeamMember, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
      }
      setSnackbar({ open: true, message: 'Team member saved successfully', severity: 'success' });
      fetchTeamMembers();
    } catch (error) {
      console.error('Error saving team member:', error);
      setSnackbar({ open: true, message: 'Failed to save team member', severity: 'error' });
    }
  };

  const handleNewTeamMember = () => {
    setSelectedTeamMember({
      teamMemberId: null,
      teamMemberName: '',
      role: '',
      contactInfo: '',
      phoneNo: '',
      emailId: '',
      expertise: '',
      relievingDate: null,
      department: null,
      company: null,
    });
  };

  const handleDeleteTeamMember = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${config.apiBaseUrl}/api/team-members/${selectedTeamMember.teamMemberId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
      });
      setSnackbar({ open: true, message: 'Team member deleted successfully', severity: 'success' });
      fetchTeamMembers();
      setSelectedTeamMember(null);
    } catch (error) {
      console.error('Error deleting team member:', error);
      setSnackbar({ open: true, message: 'Failed to delete team member', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columnDefs = [
    { headerName: 'Name', field: 'teamMemberName', sortable: true, filter: true },
    { headerName: 'Role', field: 'role', sortable: true, filter: true },
    { headerName: 'Contact Info', field: 'contactInfo', sortable: true, filter: true },
    { headerName: 'Phone No', field: 'phoneNo', sortable: true, filter: true },
    { headerName: 'Department', field: 'department.departmentName', sortable: true, filter: true },
    { headerName: 'Company', field: 'company.companyName', sortable: true, filter: true },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 150,
    filter: true,
    sortable: true,
    resizable: true,
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    if (params.api.getDisplayedRowCount() > 0) {
      const firstRow = params.api.getDisplayedRowAtIndex(0);
      params.api.selectNode(firstRow, true);
      setSelectedTeamMember(firstRow.data);
      setTimeout(() => {
        editSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <TextField
            label="Search Team Members"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ padding: 2 }}>
              <Box className="ag-theme-alpine" sx={{ height: 400, width: '100%' }}>
                <AgGridReact
                  columnDefs={columnDefs}
                  rowData={teamMembers}
                  pagination={true}
                  paginationPageSize={10}
                  onRowClicked={handleRowClick}
                  onGridReady={onGridReady}
                  defaultColDef={defaultColDef}
                  rowSelection="single"
                />
              </Box>
            </Paper>
          </Grid>
          {selectedTeamMember && (
            <Grid item xs={12}>
              <Accordion expanded ref={editSectionRef} sx={{ marginTop: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Edit Team Member</Typography>
                  <Box ml={2}>
                    <Button variant="contained" color="primary" onClick={handleSave} sx={{ marginRight: 1 }}>
                      Save
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleDeleteTeamMember} sx={{ marginRight: 1 }}>
                      Delete
                    </Button>
                    <Button variant="contained" color="default" onClick={handleNewTeamMember}>
                      New
                    </Button>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Name"
                        variant="outlined"
                        fullWidth
                        name="teamMemberName"
                        value={selectedTeamMember.teamMemberName}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Role"
                        variant="outlined"
                        fullWidth
                        name="role"
                        value={selectedTeamMember.role}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Contact Info"
                        variant="outlined"
                        fullWidth
                        name="contactInfo"
                        value={selectedTeamMember.contactInfo || ''}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Phone No"
                        variant="outlined"
                        fullWidth
                        name="phoneNo"
                        value={selectedTeamMember.phoneNo || ''}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email ID"
                        variant="outlined"
                        fullWidth
                        name="emailId"
                        value={selectedTeamMember.emailId || ''}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Expertise"
                        variant="outlined"
                        fullWidth
                        name="expertise"
                        value={selectedTeamMember.expertise || ''}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Relieving Date"
                        value={selectedTeamMember.relievingDate}
                        onChange={handleDateChange}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Department</InputLabel>
                        <Select
                          label="Department"
                          name="department"
                          value={selectedTeamMember.department ? selectedTeamMember.department.departmentId : ''}
                          onChange={handleInputChange}
                        >
                          {departments.map((department) => (
                            <MenuItem key={department.departmentId} value={department.departmentId}>
                              {department.departmentName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Company</InputLabel>
                        <Select
                          label="Company"
                          name="company"
                          value={selectedTeamMember.company ? selectedTeamMember.company.companyId : ''}
                          onChange={handleInputChange}
                        >
                          {companies.map((company) => (
                            <MenuItem key={company.companyId} value={company.companyId}>
                              {company.companyName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
        </Grid>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default TeamMembers;